/**
 * Utility for placing clothing items directly on the canvas
 * Updated to work with backend URLs and IDs for better persistence
 */

export const CanvasItemPlacer = {
  /**
   * Get the display image URL based on available images
   * Priority: generatedProductPhoto > removedBgImage (if toggled) > original image
   * @param {Object} item - The clothing item
   * @returns {string} The appropriate image URL
   */
  getDisplayImageUrl: (item) => {
    if (item.generatedProductPhoto?.url) {
      return item.generatedProductPhoto.url;
    }
    if (item.useRemovedImage && item.removedBgImage?.url) {
      return item.removedBgImage.url;
    }
    return item.image?.url || item.image;
  },

  /**
   * Calculate proper item dimensions maintaining aspect ratio
   * @param {string|Object} imageSource - The image URL or image object with url property
   * @param {number} canvasWidth - Canvas width
   * @param {number} canvasHeight - Canvas height
   * @returns {Promise<Object>} Dimensions object with width and height
   */
  calculateItemDimensions: (imageSource, canvasWidth, canvasHeight) => {
    return new Promise((resolve) => {
      // Handle both string URLs and image objects from RTK Query/backend
      const imageUrl = typeof imageSource === 'string' 
        ? imageSource 
        : imageSource?.url || imageSource?.image?.url || imageSource;
      
      if (!imageUrl) {
        console.error('No valid image URL provided to calculateItemDimensions');
        const fallbackSize = Math.min(canvasWidth / 6, 150);
        resolve({ width: fallbackSize, height: fallbackSize, aspectRatio: 1 });
        return;
      }
      
      const img = new Image();
      img.onload = () => {
        // Use consistent sizing logic for both click and drag
        const maxWidth = Math.min(canvasWidth / 5, 180); // Smaller default size
        const maxHeight = Math.min(canvasHeight / 3, 240);
        
        const aspectRatio = img.height / img.width;
        
        let itemWidth = maxWidth;
        let itemHeight = itemWidth * aspectRatio;
        
        // If height exceeds max, scale down proportionally
        if (itemHeight > maxHeight) {
          itemHeight = maxHeight;
          itemWidth = itemHeight / aspectRatio;
        }
        
        resolve({ width: itemWidth, height: itemHeight, aspectRatio });
      };
      
      img.onerror = () => {
        console.error('Failed to load image from backend URL:', imageUrl);
        // Fallback dimensions
        const fallbackSize = Math.min(canvasWidth / 6, 150);
        resolve({ width: fallbackSize, height: fallbackSize, aspectRatio: 1 });
      };
      
      img.src = imageUrl;
    });
  },

  /**
   * Add an item to the center of the canvas with proper aspect ratio
   * @param {Object} item - The clothing item to add
   * @param {Array} droppedItems - Current items on canvas
   * @param {number} canvasWidth - Canvas width
   * @param {number} canvasHeight - Canvas height
   * @returns {Promise<Object>} New item with position and sizing
   */
  addItemToCenter: async (item, droppedItems, canvasWidth, canvasHeight) => {
    // Get the correct display image URL based on useRemovedImage flag
    const displayImageUrl = CanvasItemPlacer.getDisplayImageUrl(item);
    
    // Calculate proper dimensions using the display image
    const dimensions = await CanvasItemPlacer.calculateItemDimensions(
      displayImageUrl, 
      canvasWidth, 
      canvasHeight
    );
    
    // Calculate center position
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    // Position item so its center is at canvas center
    const x = centerX - (dimensions.width / 2);
    const y = centerY - (dimensions.height / 2);
    
    // Get next z-index
    const newZIndex = droppedItems.length > 0 
      ? Math.max(...droppedItems.map(item => item.zIndex || 0)) + 1 
      : 1;
    
    const newItem = {
      ...item,
      // Override image with display URL for canvas rendering
      image: displayImageUrl,
      x,
      y,
      width: dimensions.width,
      height: dimensions.height,
      rotation: 0,
      zIndex: newZIndex,
      isVirtualTryOn: false
    };
    
    console.log('✅ Item centered with proper aspect ratio:', {
      itemName: item.clothesName || item.id,
      dimensions,
      position: { x, y },
      canvasSize: { width: canvasWidth, height: canvasHeight },
      useRemovedImage: item.useRemovedImage,
      displayImageUrl: displayImageUrl?.substring(0, 80)
    });
    
    return newItem;
  },

  /**
   * Add an item to canvas with smart positioning to avoid overlaps
   * @param {Object} item - The clothing item to add
   * @param {Array} droppedItems - Current items on canvas
   * @param {number} canvasWidth - Canvas width
   * @param {number} canvasHeight - Canvas height
   * @returns {Promise<Object>} New item with position and sizing
   */
  addItemWithSmartPositioning: async (item, droppedItems, canvasWidth, canvasHeight) => {
    const baseItem = await CanvasItemPlacer.addItemToCenter(item, droppedItems, canvasWidth, canvasHeight);
    
    // If no items on canvas, use center position
    if (droppedItems.length === 0) {
      return baseItem;
    }
    
    // Check for overlaps and adjust position if needed
    const hasOverlap = (newItem, existingItems) => {
      return existingItems.some(existing => {
        const overlapX = newItem.x < existing.x + existing.width && 
                        newItem.x + newItem.width > existing.x;
        const overlapY = newItem.y < existing.y + existing.height && 
                        newItem.y + newItem.height > existing.y;
        return overlapX && overlapY;
      });
    };
    
    // Try different positions in a spiral pattern from center
    const positions = [
      { x: 0, y: 0 }, // Center (original)
      { x: 50, y: 0 }, { x: -50, y: 0 }, { x: 0, y: 50 }, { x: 0, y: -50 },
      { x: 50, y: 50 }, { x: -50, y: -50 }, { x: 50, y: -50 }, { x: -50, y: 50 },
      { x: 100, y: 0 }, { x: -100, y: 0 }, { x: 0, y: 100 }, { x: 0, y: -100 }
    ];
    
    for (const offset of positions) {
      const testItem = {
        ...baseItem,
        x: baseItem.x + offset.x,
        y: baseItem.y + offset.y
      };
      
      // Check if position is within canvas bounds
      if (testItem.x >= 0 && testItem.y >= 0 && 
          testItem.x + testItem.width <= canvasWidth && 
          testItem.y + testItem.height <= canvasHeight) {
        
        // Check if position doesn't overlap with existing items
        if (!hasOverlap(testItem, droppedItems)) {
          return testItem;
        }
      }
    }
    
    // If no non-overlapping position found, use center anyway
    return baseItem;
  },

  /**
   * Create item for drag and drop with consistent sizing
   * @param {Object} item - The clothing item to add
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {Array} droppedItems - Current items on canvas
   * @param {number} canvasWidth - Canvas width
   * @param {number} canvasHeight - Canvas height
   * @returns {Promise<Object>} New item with position and sizing
   */
  createDragDropItem: async (item, x, y, droppedItems, canvasWidth, canvasHeight) => {
    // Get the correct display image URL based on useRemovedImage flag
    const displayImageUrl = CanvasItemPlacer.getDisplayImageUrl(item);
    
    // Use same dimensions calculation as click
    const dimensions = await CanvasItemPlacer.calculateItemDimensions(
      displayImageUrl, 
      canvasWidth, 
      canvasHeight
    );
    
    // Get next z-index
    const newZIndex = droppedItems.length > 0 
      ? Math.max(...droppedItems.map(item => item.zIndex || 0)) + 1 
      : 1;
    
    const newItem = {
      ...item,
      // Override image with display URL for canvas rendering
      image: displayImageUrl,
      x,
      y,
      width: dimensions.width,
      height: dimensions.height,
      rotation: 0,
      zIndex: newZIndex,
      isVirtualTryOn: false
    };
    
    console.log('✅ Drag drop item created with consistent sizing:', {
      itemName: item.clothesName || item.id,
      dimensions,
      position: { x, y },
      useRemovedImage: item.useRemovedImage
    });
    
    return newItem;
  },

  /**
   * Serialize canvas state to use backend IDs and URLs instead of base64 data
   * @param {Array} canvasItems - Items currently on canvas
   * @param {Object} backgroundState - Current background state
   * @param {Object} personImageState - Current person image state
   * @returns {Object} Serialized canvas state with backend references
   */
  serializeCanvasState: (canvasItems, backgroundState, personImageState) => {
    const serializedItems = canvasItems.map(item => ({
      // Backend references
      id: item.id,
      backendId: item.backendId || item.id,
      imageUrl: item.image?.url || item.imageUrl || item.image,
      
      // Item properties
      clothesName: item.clothesName || item.name,
      type: item.type,
      
      // Canvas positioning
      x: item.x,
      y: item.y,
      width: item.width,
      height: item.height,
      rotation: item.rotation || 0,
      zIndex: item.zIndex || 0,
      
      // VTO metadata
      isVirtualTryOn: item.isVirtualTryOn || false,
      tryOnMetadata: item.tryOnMetadata || null,
      
      // Timestamps
      addedAt: item.addedAt || Date.now(),
      lastModified: Date.now()
    }));

    const serializedBackground = backgroundState ? {
      id: backgroundState.id,
      imageUrl: backgroundState.image?.url || backgroundState.imageUrl || backgroundState.image,
      type: backgroundState.type || 'custom',
      name: backgroundState.name,
      category: backgroundState.category
    } : null;

    const serializedPersonImage = personImageState ? {
      id: personImageState.activePersonImageId || personImageState.id,
      imageUrl: personImageState.image?.url || personImageState.imageUrl || personImageState.image,
      isVirtualTryOnResult: personImageState.isVirtualTryOnResult || false,
      position: personImageState.position || { x: 0, y: 0 },
      scale: personImageState.scale || 1.0,
      metadata: personImageState.virtualTryOnMetadata || null
    } : null;

    return {
      items: serializedItems,
      background: serializedBackground,
      personImage: serializedPersonImage,
      version: '2.0', // Updated version for backend URL storage
      savedAt: Date.now(),
      itemCount: serializedItems.length
    };
  },

  /**
   * Restore canvas state from backend data
   * @param {Object} serializedState - Serialized canvas state
   * @returns {Object} Restored canvas state with proper item structure
   */
  restoreCanvasState: (serializedState) => {
    if (!serializedState || typeof serializedState !== 'object') {
      return {
        items: [],
        background: null,
        personImage: null,
        restored: false
      };
    }

    // Handle version compatibility
    const isOldFormat = !serializedState.version || serializedState.version.startsWith('1.');
    
    const restoredItems = (serializedState.items || []).map(item => ({
      // Restore backend references
      id: item.backendId || item.id,
      backendId: item.backendId || item.id,
      image: item.imageUrl, // Use URL from backend
      imageUrl: item.imageUrl,
      
      // Restore item properties
      clothesName: item.clothesName || item.name,
      name: item.clothesName || item.name,
      type: item.type,
      
      // Restore canvas positioning
      x: item.x || 0,
      y: item.y || 0,
      width: item.width || 150,
      height: item.height || 150,
      rotation: item.rotation || 0,
      zIndex: item.zIndex || 0,
      
      // Restore VTO metadata
      isVirtualTryOn: item.isVirtualTryOn || false,
      tryOnMetadata: item.tryOnMetadata || null,
      
      // Restore timestamps
      addedAt: item.addedAt || Date.now(),
      lastModified: item.lastModified || Date.now()
    }));

    const restoredBackground = serializedState.background ? {
      id: serializedState.background.id,
      image: serializedState.background.imageUrl,
      imageUrl: serializedState.background.imageUrl,
      type: serializedState.background.type || 'custom',
      name: serializedState.background.name,
      category: serializedState.background.category
    } : null;

    const restoredPersonImage = serializedState.personImage ? {
      activePersonImageId: serializedState.personImage.id,
      image: serializedState.personImage.imageUrl,
      imageUrl: serializedState.personImage.imageUrl,
      isVirtualTryOnResult: serializedState.personImage.isVirtualTryOnResult || false,
      position: serializedState.personImage.position || { x: 0, y: 0 },
      scale: serializedState.personImage.scale || 1.0,
      virtualTryOnMetadata: serializedState.personImage.metadata || null
    } : null;

    console.log('🔄 Canvas state restored from backend data:', {
      itemCount: restoredItems.length,
      hasBackground: !!restoredBackground,
      hasPersonImage: !!restoredPersonImage,
      version: serializedState.version || 'legacy'
    });

    return {
      items: restoredItems,
      background: restoredBackground,
      personImage: restoredPersonImage,
      restored: true,
      version: serializedState.version || '2.0-converted'
    };
  },

  /**
   * Save canvas state to localStorage with backend references
   * @param {Object} canvasState - Canvas state to save
   * @param {string} key - localStorage key
   * @returns {boolean} Success status
   */
  saveCanvasStateToStorage: (canvasState, key = 'canvas_state') => {
    try {
      const serializedState = JSON.stringify(canvasState);
      
      // Check size (should be much smaller with URLs)
      if (serializedState.length > 100000) { // 100KB limit
        console.warn('Canvas state is large, compressing...');
        // Keep only essential data
        const compressedState = {
          ...canvasState,
          items: canvasState.items.slice(0, 10), // Keep only 10 items
          version: canvasState.version + '-compressed'
        };
        localStorage.setItem(key, JSON.stringify(compressedState));
      } else {
        localStorage.setItem(key, serializedState);
      }
      
      console.log('💾 Canvas state saved to localStorage (URLs only)');
      return true;
    } catch (error) {
      console.error('❌ Failed to save canvas state:', error);
      return false;
    }
  },

  /**
   * Load canvas state from localStorage
   * @param {string} key - localStorage key
   * @returns {Object|null} Canvas state or null
   */
  loadCanvasStateFromStorage: (key = 'canvas_state') => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      const parsed = JSON.parse(stored);
      return CanvasItemPlacer.restoreCanvasState(parsed);
    } catch (error) {
      console.error('❌ Failed to load canvas state:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(key);
      } catch (clearError) {
        console.warn('Failed to clear corrupted canvas state:', clearError);
      }
      return null;
    }
  }
};

export default CanvasItemPlacer;